import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { width?: 'sm' | 'md' | 'lg' }
>(({ className, children, width = 'md', ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in" style={{ background: 'var(--c-scrim)' }} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 max-h-[88vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto',
        'animate-pop-in rounded-sm border border-rule-strong bg-surface shadow-pop',
        width === 'sm' && 'sm:max-w-md',
        width === 'md' && 'sm:max-w-xl',
        width === 'lg' && 'sm:max-w-3xl',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = 'DialogContent';

export function DialogHeader({ title, description }: { title: string; description?: React.ReactNode }) {
  return (
    <div className="border-b border-rule px-4 py-3">
      <DialogPrimitive.Title className="text-lg font-semibold tracking-[-0.01em] text-ink">{title}</DialogPrimitive.Title>
      {description ? (
        <DialogPrimitive.Description asChild>
          <div className="mt-1 max-w-prose text-sm leading-[19px] text-ink-2">{description}</div>
        </DialogPrimitive.Description>
      ) : null}
    </div>
  );
}

export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-4 px-4 py-4', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-ground px-4 py-3', className)}
      {...props}
    />
  );
}
